import {
  AlertCircle,
  ArrowLeft,
  Check,
  Clock3,
  Download,
  LoaderCircle,
  Monitor,
  RefreshCw,
  UserPlus,
  WifiOff,
  X,
} from "lucide-react";
import { cloneElement, useEffect, useId, useRef, useState, type FormEvent, type ReactElement, type ReactNode } from "react";
import { ApiClientError, api, postJson } from "./api";
import { useResource } from "./hooks";

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="brand" aria-label="InfraTime">
      <span className="brand-mark" aria-hidden="true"><span /></span>
      {!compact && <span>InfraTime</span>}
    </div>
  );
}

export function Button({
  children,
  variant = "primary",
  icon,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "quiet" | "danger"; icon?: ReactNode }) {
  return <button className={`button button-${variant}`} {...props}>{icon}{children}</button>;
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return <span className="spinner" role="status"><LoaderCircle aria-hidden="true" /><span className="sr-only">{label}</span></span>;
}

export function PageLoading({ message }: { message: string }) {
  return (
    <div className="state-panel state-loading" role="status">
      <span className="skeleton-icon"><LoaderCircle aria-hidden="true" /></span>
      <div><strong>{message}</strong><span>This should only take a moment.</span></div>
    </div>
  );
}

export function ErrorState({ error, retry, compact = false }: { error: Error; retry: () => void; compact?: boolean }) {
  const offline = error instanceof ApiClientError && error.code === "NETWORK_ERROR";
  return (
    <div className={`state-panel state-error ${compact ? "state-compact" : ""}`} role="alert">
      <span className="state-icon">{offline ? <WifiOff aria-hidden="true" /> : <AlertCircle aria-hidden="true" />}</span>
      <div>
        <strong>{offline ? "You appear to be offline" : "We couldn’t load this area"}</strong>
        <span>{error.message}</span>
      </div>
      <Button variant="secondary" onClick={retry} icon={<RefreshCw aria-hidden="true" />}>Try again</Button>
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: { title: string; message: string; action?: { label: string; onClick: () => void } | undefined }) {
  return (
    <div className="empty-state">
      <span className="empty-illustration" aria-hidden="true"><Clock3 /><i /><b /></span>
      <strong>{title}</strong>
      <p>{message}</p>
      {action && <Button onClick={action.onClick} icon={<UserPlus aria-hidden="true" />}>{action.label}</Button>}
    </div>
  );
}

export function Field({
  label,
  name,
  error,
  hint,
  children,
}: { label: string; name: string; error?: string | undefined; hint?: string | undefined; children: ReactElement<Record<string, unknown>> }) {
  const hintId = useId();
  const errorId = useId();
  const descriptionId = error ? errorId : hint ? hintId : undefined;
  const control = cloneElement(children, {
    id: children.props.id ?? name,
    "aria-describedby": descriptionId,
    "aria-invalid": error ? true : children.props["aria-invalid"],
  });
  return (
    <label className={`field ${error ? "field-error" : ""}`} htmlFor={name}>
      <span>{label}</span>
      {control}
      {hint && !error && <small id={hintId}>{hint}</small>}
      {error && <small id={errorId} role="alert">{error}</small>}
    </label>
  );
}

export function Dialog({
  open,
  title,
  description,
  onClose,
  children,
}: { open: boolean; title: string; description?: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const trigger = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      trigger.current = document.activeElement as HTMLElement | null;
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClose={() => { trigger.current?.focus(); if (open) onClose(); }}
      onClick={(event) => { if (event.target === ref.current) onClose(); }}
    >
      <div className="dialog-card">
        <header>
          <div><h2 id={titleId}>{title}</h2>{description && <p id={descriptionId}>{description}</p>}</div>
          <button className="icon-button" onClick={onClose} aria-label="Close dialog"><X aria-hidden="true" /></button>
        </header>
        {children}
      </div>
    </dialog>
  );
}

export function StatusPill({ state }: { state: string }) {
  const labels: Record<string, string> = {
    active: "Tracking",
    idle: "Idle",
    paused: "Paused",
    offline: "Offline",
    not_enrolled: "Not enrolled",
    invited: "Invited",
    disabled: "Disabled",
    pending: "Pending",
    open: "Open",
    submitted: "In employee review",
    closed: "Closed",
    paid: "Paid",
    approved: "Approved",
    eligible: "Approved",
    disputed: "Disputed",
    rejected: "Rejected",
    claimed: "Enrolled",
    expired: "Expired",
    revoked: "Revoked",
    stopped: "Stopped",
    stale: "Stale",
    unknown: "Unknown",
  };
  return <span className={`status status-${state}`}><i aria-hidden="true" />{labels[state] ?? state}</span>;
}

export function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.round(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
}

export function formatDateTime(value: string | null, options?: Intl.DateTimeFormatOptions): string {
  if (!value) return "Not yet";
  return new Intl.DateTimeFormat(undefined, options ?? { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function EnrollmentLanding({ code, onBack }: { code: string; onBack: () => void }) {
  const preview = useResource(() => postJson<{
    organizationName: string;
    employeeName: string;
    email: string;
    expiresAt: string;
    collectWindowTitles: boolean;
    evidenceCaptureEnabled: boolean;
    evidenceCaptureMinSeconds: number;
    evidenceCaptureMaxSeconds: number;
    evidenceBucketSeconds: number;
    evidenceRetentionDays: number;
    evidenceUploadMaxBytes: number;
    evidenceEncryptionKeyVersion: string | null;
    evidencePolicyVersion: number;
    consentVersion: string;
    downloads: { macos: string | null; windows: string | null };
  }>("/v1/enroll/preview", { code }), [code]);
  return (
    <main className="enroll-page">
      <header><Brand /><Button variant="quiet" onClick={onBack} icon={<ArrowLeft aria-hidden="true" />}>Back to sign in</Button></header>
      <section className="enroll-card">
        {preview.state.status === "loading" && <PageLoading message="Checking your invitation…" />}
        {preview.state.status === "error" && <ErrorState error={preview.state.error} retry={preview.refresh} />}
        {preview.state.status === "success" && (
          <>
            <span className="eyebrow"><Check aria-hidden="true" />Invitation confirmed</span>
            <h1>Join {preview.state.data.organizationName}</h1>
            <p className="lede">Hi {preview.state.data.employeeName}. Install InfraTime on your work computer, then enter this one-time code.</p>
            <div className="enrollment-code" aria-label={`Enrollment code ${code}`}>{code}</div>
            <div className="download-grid">
              {preview.state.data.downloads.macos ? <a className="download-card" href={preview.state.data.downloads.macos}><Monitor aria-hidden="true" /><span><strong>Download for macOS</strong><small>Apple silicon and Intel</small></span><Download aria-hidden="true" /></a> : <span className="download-card unavailable" aria-disabled="true"><Monitor aria-hidden="true" /><span><strong>macOS installer</strong><small>Ask your employer for the current build</small></span></span>}
              {preview.state.data.downloads.windows ? <a className="download-card" href={preview.state.data.downloads.windows}><Monitor aria-hidden="true" /><span><strong>Download for Windows</strong><small>Windows 10 or later</small></span><Download aria-hidden="true" /></a> : <span className="download-card unavailable" aria-disabled="true"><Monitor aria-hidden="true" /><span><strong>Windows installer</strong><small>Ask your employer for the current build</small></span></span>}
            </div>
            <div className="privacy-note">
              <strong>What your employer can see</strong>
              <p>Work and idle time, foreground app names, sync status{preview.state.data.collectWindowTitles ? ", and active window titles" : ""}{preview.state.data.evidenceCaptureEnabled ? ", plus disclosed randomized primary-display evidence for evidence-required assignments" : ""}. InfraTime never records keystrokes, file contents, camera, microphone, continuous screen video, or anything while paused.</p>
            </div>
            <p className="expires">Code expires {formatDateTime(preview.state.data.expiresAt)}.</p>
          </>
        )}
      </section>
      <nav className="legal-links" aria-label="Legal and support"><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/support">Support</a></nav>
    </main>
  );
}

export function AuthScreen({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      if (mode === "login") {
        await postJson("/v1/auth/login", { email: values.email, password: values.password });
      } else {
        await postJson("/v1/auth/register", {
          organizationName: values.organizationName,
          fullName: values.fullName,
          email: values.email,
          password: values.password,
        });
      }
      onAuthenticated();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught : new ApiClientError("Could not sign in.", 0, "UNKNOWN"));
    } finally {
      setSubmitting(false);
    }
  };
  const fieldError = (name: string) => error?.fieldErrors[name]?.[0];
  return (
    <main className="auth-page">
      <section className="auth-story">
        <Brand />
        <div className="story-copy">
          <span className="eyebrow">Time you can account for</span>
          <h1>Clear workdays.<br />Fewer timesheet questions.</h1>
          <p>Track time and app activity transparently across macOS and Windows—with visible controls, consented randomized evidence when required, and no keystroke surveillance.</p>
        </div>
        <div className="story-status" aria-hidden="true">
          <div className="pulse-ring"><span /></div>
          <div><strong>Tracking stays visible</strong><small>Live status · clear sync health</small></div>
        </div>
        <small className="auth-foot">Employee-visible · Consent-led · Pause anytime</small>
      </section>
      <section className="auth-form-wrap">
        <form className="auth-form" onSubmit={submit} noValidate>
          <div><span className="eyebrow">{mode === "login" ? "Welcome back" : "Set up your workspace"}</span><h2>{mode === "login" ? "Sign in to InfraTime" : "Create your account"}</h2><p>{mode === "login" ? "Use your employer or employee account." : "You’ll become the organization owner."}</p></div>
          {error && <div className="inline-alert" role="alert"><AlertCircle aria-hidden="true" /><span><strong>{error.message}</strong>{error.requestId && <small>Reference: {error.requestId}</small>}</span></div>}
          {mode === "register" && (
            <>
              <Field label="Organization name" name="organizationName" error={fieldError("organizationName")}>
                <input id="organizationName" name="organizationName" autoComplete="organization" required maxLength={100} onBlur={() => setTouched((value) => ({ ...value, organizationName: true }))} aria-invalid={Boolean(touched.organizationName && fieldError("organizationName"))} />
              </Field>
              <Field label="Your name" name="fullName" error={fieldError("fullName")}>
                <input id="fullName" name="fullName" autoComplete="name" required maxLength={100} />
              </Field>
            </>
          )}
          <Field label="Email address" name="email" error={fieldError("email")}>
            <input id="email" name="email" type="email" inputMode="email" autoComplete="email" required maxLength={254} />
          </Field>
          <Field label="Password" name="password" error={fieldError("password")} hint={mode === "register" ? "Use at least 12 characters." : undefined}>
            <input id="password" name="password" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} required minLength={mode === "register" ? 12 : 1} maxLength={200} />
          </Field>
          <Button type="submit" disabled={submitting}>{submitting ? <><Spinner label="Signing in" />Please wait…</> : mode === "login" ? "Sign in" : "Create workspace"}</Button>
          <p className="switch-auth">{mode === "login" ? "New to InfraTime?" : "Already have an account?"} <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }}>{mode === "login" ? "Create a workspace" : "Sign in"}</button></p>
          <nav className="legal-links" aria-label="Legal and support"><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/support">Support</a></nav>
        </form>
      </section>
    </main>
  );
}
